import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { userService } from '../services/userService';
import { toast } from 'react-hot-toast';
import UserLanguageSwitcher from '../components/common/UserLanguageSwitcher';
import './MajesticCommon.css';
import MajesticSelect from '../components/common/MajesticSelect';

const AddCryptoWalletPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        network: '',
        walletAddress: '',
        qrCodeImage: ''
    });

    const [qrPreview, setQrPreview] = useState(null);

    const networkOptions = [
        { value: 'USDC', label: 'USDC', name: 'network' },
        { value: 'Binance Pay', label: 'Binance Pay', name: 'network' },
        { value: 'TRC20', label: 'TRC20', name: 'network' },
        { value: 'BEP20', label: 'BEP20', name: 'network' },
        { value: 'BTC', label: 'BTC', name: 'network' },
        { value: 'ETH', label: 'ETH', name: 'network' },
        { value: 'BNB', label: 'BNB', name: 'network' }
    ];

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                toast.error(t('image_too_large', 'Image must be less than 2MB'));
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setQrPreview(reader.result);
                setFormData(prev => ({ ...prev, qrCodeImage: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setQrPreview(null);
        setFormData(prev => ({ ...prev, qrCodeImage: '' }));
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const data = await userService.addCryptoWallet(formData);
            if (data.success) {
                toast.success(t('wallet_address_added', 'Wallet address added successfully'));
                navigate('/my-crypto-wallet');
            }
        } catch (error) {
            console.error('Failed to add crypto wallet:', error);
            toast.error(error.message || t('error_adding_wallet', 'Error adding wallet address'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="majestic-page-wrapper">
            <div className="majestic-header d-flex align-items-center px-3 py-3 justify-content-between">
                <div style={{ width: '40px' }}>
                    <i
                        className="bi bi-chevron-left text-white fs-4 cursor-pointer"
                        onClick={() => navigate(-1)}
                    ></i>
                </div>
                <h1 className="majestic-header-title flex-grow-1 text-center">
                    {t('add_wallet_address', 'Add Wallet Address')}
                </h1>
                <div style={{ width: '40px' }} className="d-flex justify-content-end">
                    <UserLanguageSwitcher />
                </div>
            </div>

            <div className="majestic-container animate-slide-up">
                <div className="p-4 rounded-4 mb-4" style={{ background: 'rgba(252, 213, 53, 0.05)', border: '1px solid rgba(252, 213, 53, 0.1)' }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="p-2 rounded-3" style={{ background: 'rgba(252, 213, 53, 0.1)' }}>
                            <i className="bi bi-shield-lock-fill text-warning fs-4"></i>
                        </div>
                        <div>
                            <div className="text-white fw-bold">{t('secure_vault', 'SECURE VAULT')}</div>
                            <div className="text-secondary small">{t('secure_vault_desc', 'Your wallet addresses are protected by military-grade encryption.')}</div>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Network */}
                    <div className="majestic-form-group">
                        <div className="majestic-label-row">
                            <span className="majestic-label-dot"></span>
                            <span className="majestic-label-text">
                                {t('network', 'Network')}
                                <span style={{ color: '#ff4d4d', marginLeft: '4px' }}>*</span>
                            </span>
                        </div>
                        <MajesticSelect
                            options={networkOptions}
                            value={formData.network}
                            onChange={handleChange}
                            placeholder={t('select_network', 'Select Network')}
                            icon="bi-hdd-network-fill"
                        />
                    </div>

                    {/* Wallet Address */}
                    <div className="majestic-form-group">
                        <div className="majestic-label-row">
                            <span className="majestic-label-dot"></span>
                            <label htmlFor="walletAddress" className="majestic-label-text">
                                {t('wallet_address', 'Wallet Address')}
                                <span style={{ color: '#ff4d4d', marginLeft: '4px' }}>*</span>
                            </label>
                        </div>
                        <div className="majestic-input-wrapper">
                            <input
                                id="walletAddress"
                                type="text"
                                name="walletAddress"
                                className="form-control majestic-input-field"
                                placeholder={t('enter_crypto_address', 'Enter Wallet Address')}
                                value={formData.walletAddress}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    {/* QR Code Upload */}
                    <div className="majestic-form-group mb-4">
                        <div className="majestic-label-row">
                            <span className="majestic-label-dot"></span>
                            <span className="majestic-label-text">{t('wallet_qr_code', 'Wallet Address QR Code')}</span>
                        </div>
                        <div
                            className="d-flex flex-column align-items-center justify-content-center p-3 rounded-3"
                            style={{
                                background: 'rgba(255,255,255,0.03)',
                                border: '2px dashed rgba(255,255,255,0.15)',
                                minHeight: '180px',
                                cursor: 'pointer'
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {qrPreview ? (
                                <div className="position-relative">
                                    <img
                                        src={qrPreview}
                                        alt="QR Code Preview"
                                        style={{ width: '150px', height: '150px', objectFit: 'contain', borderRadius: '8px' }}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                        style={{ transform: 'translate(50%, -50%)', borderRadius: '50%', padding: '2px 6px' }}
                                        onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
                                    >
                                        <i className="bi bi-x"></i>
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <i className="bi bi-qr-code text-secondary" style={{ fontSize: '40px' }}></i>
                                    <div className="text-secondary small mt-2">{t('click_upload_qr', 'Click to upload QR Code')}</div>
                                    <div className="text-secondary" style={{ fontSize: '10px' }}>{t('max_size', 'Max 2MB')}</div>
                                </>
                            )}
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleImageUpload}
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn majestic-btn-primary w-100 py-3 mt-2"
                    >
                        {submitting ? (
                            <><span className="spinner-border spinner-border-sm me-2"></span>{t('submitting', 'Processing...')}</>
                        ) : (
                            <><i className="bi bi-shield-check me-2"></i>{t('submit', 'Submit Address')}</>
                        )}
                    </button>
                </form>

                <div className="mt-4 p-3 rounded-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.03)' }}>
                    <div className="text-secondary extra-small d-flex gap-2">
                        <i className="bi bi-info-circle text-warning"></i>
                        <span style={{ fontSize: '11px', lineHeight: '1.4' }}>
                            {t('wallet_notice', 'Please double-check your wallet address. Incorrect addresses may result in permanent loss of funds.')}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddCryptoWalletPage;

